import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type {Breakpoint} from '@mui/material/styles';
import {useTheme} from '@mui/material/styles';
import {merge} from 'es-toolkit';
import {useBoolean} from 'minimal-shared/hooks';

import {defaultSettings} from '@minimal/components/settings';
import type {HeaderSectionProps, LayoutSectionProps} from '@minimal/layouts/core';
import {HeaderSection, LayoutSection} from '@minimal/layouts/core';

import {AccountDrawer} from '../components/account-drawer';
import {MenuButton} from '../components/menu-button';
import {layoutClasses} from '../core/classes';
import type {MainSectionProps} from '../core/main-section';
import {MainSection} from '../core/main-section';
import {dashboardLayoutVars, dashboardNavColorVars} from './css-vars';
import {NavMobile} from './nav-mobile';
import {NavVertical} from './nav-vertical';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    main?: MainSectionProps;
  };
};

export function DashboardLayout({sx, cssVars, children, slotProps, layoutQuery = 'lg'}: DashboardLayoutProps) {
  const theme = useTheme();

  const navVars = dashboardNavColorVars(theme, defaultSettings.navColor, defaultSettings.navLayout);

  const {value: open, onFalse: onClose, onTrue: onOpen} = useBoolean();

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: {
        maxWidth: false,
        sx: {
          px: {[layoutQuery]: 5},
        },
      },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{display: 'none', borderRadius: 0}}>
          This is an info Alert.
        </Alert>
      ),
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton onClick={onOpen} sx={{mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: {display: 'none'}}} />
          <NavMobile open={open} onClose={onClose} cssVars={navVars.section} />
        </>
      ),
      rightArea: (
        <Box sx={{display: 'flex', alignItems: 'center', gap: {xs: 0, sm: 0.75}}}>
          {/** @slot Account drawer */}
          <AccountDrawer />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{...headerSlots, ...slotProps?.header?.slots}}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => <NavVertical layoutQuery={layoutQuery} cssVars={navVars.section} />;

  const renderFooter = () => null;

  const renderMain = () => <MainSection {...slotProps?.main}>{children}</MainSection>;

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars}}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
